import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { v4 as uuid } from 'uuid'

import { AppLoader } from '@components/AppLoader'
import { DisplayForm, DisplayFormProps } from '@components/DisplayForm/DisplayForm'
import { useFetchRow } from '@components/DisplayForm/hooks/useFetchRow'
import { usePreFillConfig } from '@components/DisplayForm/hooks/usePreFillConfig'
import { ErrorMessage } from '@components/ErrorMessage'

import { useFetchEnrichedFormConfigByCodeQuery, useFetchObjectByCodeQuery } from '@redux/api'
import { useFetchEnrichedContainerQuery } from '@redux/api/container.api'
import { useFetchParameterPropertiesQuery } from '@redux/api/parameters.api'

import { useAppSelector } from '@hooks'
import { transformCommonParametersToValues } from '@helpers'
import { COMMON_PARAMETERS, FIELD_VALUE_TYPE } from '@constants'

export const DisplayFormLoadInitialData: FC<DisplayFormProps> = props => {
  const { dialogId, passedParams, preFill } = props

  const { formCode, globalId, typeId, objectId, event } = passedParams

  const { t } = useTranslation()

  const formHistory = useAppSelector(state => state.formHistory)[globalId] || {}

  const { links, selectedIndex } = formHistory
  const selectedLink = selectedIndex ? links[selectedIndex] : null

  const requestFormCode =
    selectedLink && 'formCode' in selectedLink ? selectedLink.formCode : formCode
  const requestObjectId =
    selectedLink && 'objectId' in selectedLink ? (selectedLink.objectId as string) : objectId
  const requestTypeId =
    selectedLink && 'typeId' in selectedLink ? (selectedLink.typeId as FIELD_VALUE_TYPE) : typeId
  const requestType = selectedLink && 'event' in selectedLink ? selectedLink.event : event

  const {
    data: formConfig,
    isFetching: isFetchingFormConfig,
    isError: isFetchingFormConfigError,
    refetch: refetchFormConfig,
  } = useFetchEnrichedFormConfigByCodeQuery(
    { code: requestFormCode, cacheId: dialogId },
    {
      skip: !requestFormCode,
      refetchOnMountOrArgChange: true,
    }
  )

  const objectCode = formConfig?.objectCode

  const { data: objectModel, isFetching: isFetchingObjectModel } = useFetchObjectByCodeQuery(
    objectCode,
    { skip: !objectCode }
  )

  const { data: formContainersConfig, isFetching: isFetchingFormContainersConfig } =
    useFetchEnrichedContainerQuery(
      { formCode: requestFormCode, cacheId: dialogId },
      {
        skip: !requestFormCode,
        refetchOnMountOrArgChange: true,
      }
    )

  const { data: commonParameters, isFetching: isFetchingCommonParameters } =
    useFetchParameterPropertiesQuery({
      code: COMMON_PARAMETERS.STATE,
    })

  const initialCommonParameters = useMemo(
    () => transformCommonParametersToValues(commonParameters),
    [commonParameters]
  )

  const instanceId = useMemo(() => {
    return uuid()
  }, [])

  const preFillConfig = usePreFillConfig({
    formConfig,
    preFillFromAnotherObject: preFill,
  })

  const { isLoadingRow, isLoadingPrefillRow } = useFetchRow({
    type: requestType,
    formCode: requestFormCode,
    objectId: requestObjectId,
    globalId,
    typeId: requestTypeId,
    formConfig,
    objectModel,
    ...preFillConfig,
  })

  if (
    isFetchingFormConfig ||
    isFetchingCommonParameters ||
    isFetchingFormContainersConfig ||
    isLoadingRow ||
    isFetchingObjectModel ||
    isLoadingPrefillRow
  ) {
    return <AppLoader />
  }

  if (isFetchingFormConfigError || !formConfig || !formContainersConfig) {
    return (
      <ErrorMessage message={t('error.configLoadError')} retryAction={() => refetchFormConfig()} />
    )
  }

  return (
    <DisplayForm
      {...props}
      formConfig={formConfig}
      formContainersConfig={formContainersConfig}
      initialCommonParameters={initialCommonParameters}
      instanceId={instanceId}
    />
  )
}
